import { useEffect, useState } from 'react';
import { formatCurrency } from '@/lib/format.js';
import { Badge } from '@/components/ui/badge.jsx';
import { getQueue } from '@/services/queue.service.js';
import { listDealMarkets } from '@/services/markets.service.js';
import { getQuotesForAccount } from '@/services/quotes.service.js';
import { getAccountCommsTimeline } from '@/services/account.service.js';
import { partitionQueue, waitingSummary, hasRealDoor } from '@/lib/needs-you.js';
import { formatDateTime } from '@/lib/date-display.js';
import { DEAL_QUEUE_PARTIAL } from '@/lib/queue-labels.js';

// Cockpit V-B4 right rail (Ship 1.2). Feeds the shell rail slot, top to
// bottom: Needs you · Markets quickview · Act · Recent. The main pane swaps to
// the markets work surface on focus (Ship 1.3) while this rail stays put.
//   - Needs you: interim = the existing what's-waiting signals for this deal
//     (Ship 4 replaces the source with GET /needs-you). Never an empty card.
//   - Markets: carrier + status, quoted-row premium (joined from the deal's
//     quotes); the whole card clicks into the markets focus.
//   - Act: client comms only — enter a quote / text-or-email the client.
//   - Recent: the last few activity_log events for the deal.

const STATE_LABEL = {
  sent: 'Sent', acknowledged: 'Ack', working: 'Working', quoted: 'Quoted',
  declined: 'Decl', blocked: 'Blocked', no_response: 'No response',
};
const STATE_VARIANT = {
  sent: 'outline', acknowledged: 'info', working: 'info', quoted: 'success',
  declined: 'muted', blocked: 'warning', no_response: 'warning',
};

function Card({ title, action, accentLeft, children }) {
  return (
    <div className={`rounded-card border border-border bg-bg p-3 ${accentLeft ? 'border-l-2 border-l-warning' : ''}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-dim">{title}</h3>
        {action || null}
      </div>
      {children}
    </div>
  );
}

const fmtTime = (iso) => formatDateTime(iso, { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export default function DealCockpitRail({ dealId, accountId, refreshKey, onFocusMarkets, onEnterQuote, onComposeClient, onNeedsAction }) {
  const [queue, setQueue] = useState(null);
  const [source_errors, setSourceErrors] = useState([]); // Q13: a failed or cut queue source, said in one line
  const [markets, setMarkets] = useState(null);
  const [premiumByCarrier, setPremiumByCarrier] = useState({});
  const [recent, setRecent] = useState(null);

  useEffect(() => {
    if (!dealId) return undefined;
    let dead = false;
    getQueue({ dealId })
      .then((d) => { if (!dead) { setQueue(d?.items || []); setSourceErrors(Array.isArray(d?.source_errors) ? d.source_errors : []); } })
      .catch(() => { if (!dead) { setQueue([]); setSourceErrors([]); } });
    return () => { dead = true; };
  }, [dealId, refreshKey]);

  useEffect(() => {
    if (!dealId) return undefined;
    let dead = false;
    listDealMarkets(dealId)
      .then((d) => { if (!dead) setMarkets(d?.markets || []); })
      .catch(() => { if (!dead) setMarkets([]); });
    return () => { dead = true; };
  }, [dealId, refreshKey]);

  // Join the deal's quotes onto the market rows so a quoted carrier shows its
  // (lowest) premium, like the mockup. Premiums live on quotes, not markets.
  useEffect(() => {
    if (!accountId) return undefined;
    let dead = false;
    getQuotesForAccount(accountId)
      .then((r) => {
        const qs = (r?.quotations || r?.quotes || []).filter((q) => q.deal_id === dealId);
        // KEYED ON carrier_ref_id, NOT ON A DISPLAY NAME (prompt 76 item 3).
        // This bucketed on `(q.carrier || '').toLowerCase().trim()` and looked
        // the premium up under the market's `carrier_name` — two strings that
        // are not the same string. `carrier` on this payload is
        // `q.underwriter || q.carrier`, so it is frequently a PERSON'S name,
        // while `carrier_name` is carriers.name. Even setting that aside,
        // quotes.carrier spells one carrier several ways: Starr appears as
        // starr / Starr / STARR / "Starr Aviation" / "Starr Insurance", Old
        // Republic four ways over 30 quotes. Grouping on it splits one carrier
        // into several buckets and the rail shows a badge where a premium
        // belongs. carrier_ref_id is the Supabase FK on both sides — the same
        // fix MarketsWorkSurface already made one component away.
        //
        // A quote whose carrier never resolved has no key and is skipped, not
        // bucketed under '', which would hand one arbitrary premium to whichever
        // unresolved row asked first. 42 of 386 live quotes have no carrier_id.
        const map = {};
        for (const q of qs) {
          if (q.carrier_ref_id == null) continue;
          const key = String(q.carrier_ref_id);
          const p = q.premium != null ? Number(q.premium) : null;
          if (p != null && !Number.isNaN(p) && (map[key] == null || p < map[key])) map[key] = p;
        }
        if (!dead) setPremiumByCarrier(map);
      })
      .catch(() => {});
    return () => { dead = true; };
  }, [accountId, dealId, refreshKey]);

  useEffect(() => {
    if (!accountId || !dealId) return undefined;
    let dead = false;
    getAccountCommsTimeline(accountId, dealId)
      .then((d) => {
        const entries = Array.isArray(d) ? d : (d?.timeline || d?.entries || []);
        if (!dead) setRecent(entries.slice(0, 5));
      })
      .catch(() => { if (!dead) setRecent([]); });
    return () => { dead = true; };
  }, [accountId, dealId, refreshKey]);

  // "Needs you" promises work. Only section now/today, minus the FYI kinds,
  // is work; `waiting` collapses to one muted line below (see lib/needs-you).
  const { tasks } = partitionQueue(queue || []);
  const waiting = waitingSummary(queue || []);
  const mkts = markets || [];

  return (
    <div className="space-y-3 p-3 text-text">
      {/* The left accent is an alarm colour. It only earns its place when
          something is actually waiting on David, so it follows tasks, not the
          card's mere existence. */}
      <Card title="Needs you" accentLeft={tasks.length > 0}>
        {tasks.length === 0 ? (
          <p className="py-0.5 text-[12px] text-text-muted">Nothing needs you.</p>
        ) : (
          tasks.slice(0, 6).map((item) => (
            <div key={item.key} className="flex items-center justify-between gap-2 border-b border-border py-2 last:border-0">
              <span className="text-[12px] leading-snug">
                {item.title}
                {item.detail ? <span className="text-text-dim"> · {item.detail}</span> : null}
              </span>
              {/* A control that navigates to the page you are already on is a
                  dead control. Render it only when the door goes somewhere. */}
              {hasRealDoor(item, dealId) ? (
                <button
                  type="button"
                  onClick={() => onNeedsAction?.(item)}
                  className="shrink-0 rounded border border-border px-2 py-0.5 text-[11px] hover:border-border-strong hover:bg-surface-strong"
                >
                  Go
                </button>
              ) : null}
            </div>
          ))
        )}
        {/* One line, no button: informational by definition, and the Markets
            card directly below already lists every carrier and its state. */}
        {waiting ? (
          <p className={`text-[11px] text-text-dim ${tasks.length > 0 ? 'mt-2 border-t border-border pt-2' : 'py-0.5'}`}>
            {waiting.text}
          </p>
        ) : null}
        {source_errors.length > 0 && (
          <p className="mt-2 border-t border-border pt-2 text-[11px] text-warning">{DEAL_QUEUE_PARTIAL}</p>
        )}
      </Card>

      {/* Always render (B1): emptiness is a filter, never a render gate. The
          whole card clicks into the work surface, so the "Log a submission"
          door there stays reachable even before any submission exists. */}
      <button
        type="button"
        onClick={onFocusMarkets}
        className="block w-full rounded-card text-left hover:opacity-90"
        title="Open the markets work surface"
      >
        <Card title="Markets" action={<span className="text-[11px] text-text-dim">focus ↗</span>}>
          {mkts.length === 0 ? (
            <p className="py-0.5 text-[12px] text-text-muted">No submissions logged yet.</p>
          ) : (
            mkts.map((m) => {
              const prem = m.carrier_ref_id != null ? premiumByCarrier[String(m.carrier_ref_id)] : undefined;
              return (
                <div key={m.id} className="flex items-center justify-between gap-2 border-b border-border py-1.5 last:border-0">
                  <span className="min-w-0 flex-1 truncate text-[12px] font-medium">{m.carrier_name || 'Ad-hoc'}</span>
                  {m.state === 'quoted' && prem != null ? (
                    <span className="text-[12.5px] font-bold text-success">{formatCurrency(prem)}</span>
                  ) : (
                    <Badge variant={STATE_VARIANT[m.state] || 'outline'} className="text-[9px]">
                      {STATE_LABEL[m.state] || m.state}
                    </Badge>
                  )}
                </div>
              );
            })
          )}
        </Card>
      </button>

      <Card title="Act">
        <button
          type="button"
          onClick={onEnterQuote}
          className="mb-1.5 w-full rounded-[5px] border border-accent bg-accent px-3 py-1.5 text-[12px] font-medium text-white hover:brightness-110"
        >
          Enter a quote
        </button>
        <button
          type="button"
          onClick={onComposeClient}
          className="w-full rounded-[5px] border border-border px-3 py-1.5 text-[12px] hover:border-border-strong hover:bg-surface-strong"
        >
          Text / email client
        </button>
      </Card>

      {recent && recent.length > 0 ? (
        <Card title="Recent">
          {recent.map((e, i) => (
            <div key={i} className="border-b border-border py-1.5 text-[12px] last:border-0">
              <span>{e.summary || e.title || 'Activity'}</span>
              {e.occurred_at ? <span className="text-text-dim"> · {fmtTime(e.occurred_at)}</span> : null}
            </div>
          ))}
        </Card>
      ) : null}
    </div>
  );
}
